import { calcAVWAP, detectInstitutionalPatterns } from "./patterns";

// Backtest av kurs- og volummønstre.
// Inngang: åpningskurs dagen etter signalet. Utgang: sluttkurs h handelsdager etter signalet.
// Edge = signalets snitt minus baseline (innganger hver 5. dag i de samme aksjene), med t-verdi
// så man kan skille en ekte fordel fra tilfeldig støy. Bearish signaler måles motsatt vei.

const HZ = [5, 10, 20];
const dayKey = ts => new Date(ts * 1000).toISOString().slice(0, 10);

function describe(arr) {
  if (!arr.length) return { count: 0, avg: null, median: null, winRate: null, sd: null };
  const s = [...arr].sort((a, b) => a - b), n = arr.length;
  const avg = arr.reduce((a, b) => a + b, 0) / n;
  const sd = n > 1 ? Math.sqrt(arr.reduce((a, b) => a + (b - avg) ** 2, 0) / (n - 1)) : null;
  return {
    count: n, avg, sd,
    median: n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2,
    winRate: arr.filter(v => v > 0).length / n * 100,
  };
}
const mean = a => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);

export function calcBacktest(dataMap, tickers, signalTypes, opts = {}) {
  const { avwapFilter = false, cooldownDays = 20, confluenceFilter = false, confluenceWindow = 3,
    costPct = 0, bias = {}, spy = null, targetPct = 5 } = opts;

  // Markedsregime per dato: S&P 500 over 200-dagers snitt = opptrend
  const upRegime = {};
  if (spy?.closes?.length) {
    let sum = 0;
    spy.closes.forEach((c, i) => {
      sum += c ?? 0;
      if (i >= 200) sum -= spy.closes[i - 200] ?? 0;
      if (i >= 199 && c != null) upRegime[dayKey(spy.timestamps[i])] = c > sum / 200;
    });
  }

  const trades = Object.fromEntries(signalTypes.map(t => [t, []]));
  const base = [];
  const tradeAt = (closes, opens, sigIdx, date) => {
    const entryIdx = sigIdx + 1;
    if (entryIdx >= closes.length || opens[entryIdx] == null) return null;
    const entry = opens[entryIdx], r = {};
    for (const h of HZ) {
      const x = closes[sigIdx + h];
      if (sigIdx + h < closes.length && x != null) r[h] = (x / entry - 1) * 100 - costPct;
    }
    const x20 = closes[sigIdx + 20];
    return { r, up: upRegime[date], hit: x20 != null ? x20 >= entry * (1 + targetPct / 100) : null };
  };

  tickers.forEach(ticker => {
    const td = dataMap[ticker];
    if (!td?.closes?.length || !td.timestamps?.length) return;
    const { closes, opens, highs, lows, volumes, timestamps } = td;
    const n = closes.length;

    let avwapArr = null;
    if (avwapFilter) {
      const anchorIdx = volumes.reduce((best, v, i) => (v || 0) > (volumes[best] || 0) ? i : best, 0);
      avwapArr = calcAVWAP(closes, highs, lows, volumes, anchorIdx);
    }

    const patterns = detectInstitutionalPatterns(closes, opens || [], highs || [], lows || [], volumes || [], timestamps);
    const dateToIdx = {};
    timestamps.forEach((ts, i) => { dateToIdx[dayKey(ts)] = i; });

    const confluent = {};
    if (confluenceFilter) {
      patterns.forEach(p => {
        const si = dateToIdx[p.date];
        if (si == null) return;
        const found = patterns.some(q => q.type !== p.type && dateToIdx[q.date] != null && Math.abs(dateToIdx[q.date] - si) <= confluenceWindow);
        (confluent[si] ||= {})[p.type] = found;
      });
    }

    const lastSig = {};
    patterns.forEach(p => {
      if (!trades[p.type]) return;
      const sigIdx = dateToIdx[p.date];
      if (sigIdx == null) return;
      if (confluenceFilter && !confluent[sigIdx]?.[p.type]) return;
      if (lastSig[p.type] != null && sigIdx - lastSig[p.type] < cooldownDays) return;
      lastSig[p.type] = sigIdx;
      if (avwapArr) {
        const av = avwapArr[sigIdx];
        if (av != null && closes[sigIdx] != null && closes[sigIdx] < av) return;
      }
      const tr = tradeAt(closes, opens, sigIdx, p.date);
      if (tr) trades[p.type].push(tr);
    });

    // Baseline: inngang hver 5. handelsdag (etter 200 dager, som signalene som bruker 200-dagers snitt)
    for (let i = Math.min(200, Math.floor(n / 3)); i < n - 1; i += 5) {
      const tr = tradeAt(closes, opens, i, dayKey(timestamps[i]));
      if (tr) base.push(tr);
    }
  });

  const baseBy = (h, f = () => true) => base.filter(f).map(b => b.r[h]).filter(v => v != null);
  const baseline = Object.fromEntries(HZ.map(h => [`h${h}`, describe(baseBy(h))]));
  baseline.hit = mean(base.filter(b => b.hit != null).map(b => (b.hit ? 1 : 0)));
  const base10Up = mean(baseBy(10, b => b.up === true)), base10Down = mean(baseBy(10, b => b.up === false));

  const results = {};
  for (const type of signalTypes) {
    const tr = trades[type], sign = bias[type] === "bearish" ? -1 : 1;
    const res = Object.fromEntries(HZ.map(h => [`h${h}`, describe(tr.map(t => t.r[h]).filter(v => v != null))]));
    const r10 = res.h10;
    res.edge10 = r10.avg != null && baseline.h10.avg != null ? sign * (r10.avg - baseline.h10.avg) : null;
    res.t10 = res.edge10 != null && r10.sd ? res.edge10 / (r10.sd / Math.sqrt(r10.count)) : null;
    const up = tr.filter(t => t.up === true).map(t => t.r[10]).filter(v => v != null);
    const down = tr.filter(t => t.up === false).map(t => t.r[10]).filter(v => v != null);
    res.edgeUp = up.length && base10Up != null ? sign * (mean(up) - base10Up) : null;
    res.edgeDown = down.length && base10Down != null ? sign * (mean(down) - base10Down) : null;
    res.nUp = up.length; res.nDown = down.length;
    const hits = tr.filter(t => t.hit != null);
    res.hit = hits.length ? mean(hits.map(t => (t.hit ? 1 : 0))) : null;
    results[type] = res;
  }
  return { results, baseline };
}

// Hvor mye vi kan stole på en edge: t ≥ 2 og minst 30 treff er vanlig tommelfingerregel.
export function reliability(res) {
  const n = res?.h10?.count || 0, t = res?.t10;
  if (n < 15 || t == null) return { label: "for få treff", color: "#475569" };
  if (n >= 30 && t >= 2) return { label: "pålitelig", color: "#10b981" };
  if (t >= 1.5) return { label: "lovende", color: "#f59e0b" };
  if (t <= -1.5) return { label: "virker motsatt", color: "#f43f5e" };
  return { label: "støy", color: "#64748b" };
}
